import { derived } from 'easy-signal';
import { cachedValues } from '../lib/cachedValues';
import { getDocConfig } from '../lib/config';
import { agreeableServer } from '../lib/server';
import { createDocSyncer } from './docSyncer';
const INACTIVE_TIMEOUT = 1000 * 60 * 10; // 10 minutes
const PING_INTERVAL = 45000;
const PING_TIMEOUT = 5000;
export async function agreeableSyncer(config, options, tab, db) {
    const { uid, appVersion = '0.0.0' } = options;
    const cleanups = new Set();
    const subbed = new Set();
    const docSyncers = new Map();
    let isSyncing = false;
    let shouldConnect = false;
    let serverConnecting = false;
    let activityTimeout;
    const { deviceId, serverTimeOffset } = await cachedValues.get('connectionData', {});
    const server = agreeableServer(config.domain, uid, appVersion, deviceId, serverTimeOffset);
    globalThis.server = server;
    const authed = derived(() => server.state.get().authed);
    // Keep the server offset up to date
    const connectionData = derived(prev => {
        const { deviceId, serverTimeOffset } = server.state.get();
        return prev.deviceId === deviceId || prev.serverTimeOffset === serverTimeOffset
            ? prev
            : { deviceId, serverTimeOffset };
    }, { deviceId, serverTimeOffset });
    connectionData.subscribe(data => cachedValues.set('connectionData', data));
    return {
        start() {
            cleanups.add(() => server.close());
            cleanups.add(server.state.subscribe(state => updateState(state)));
            cleanups.add(authed.subscribe(onAuth));
            cleanups.add(server.onMessage(onMessage));
            cleanups.add(startPinging());
            connect();
        },
        stop() {
            cleanups.forEach(close => close());
            cleanups.clear();
        },
        now() {
            return server.getNow();
        },
        auth,
        sync,
        unsync,
        changesCreated,
        docDeleted,
        hadActivity,
        connect,
        disconnect,
    };
    function connect() {
        shouldConnect = true;
        serverConnecting = true;
        server.connect().catch(err => { }); // will automatically retry if it fails
        hadActivity();
    }
    function disconnect() {
        shouldConnect = false;
        server.disconnect();
    }
    function updateState(updates) {
        tab.setState({ ...tab.getState(), ...updates });
    }
    function startPinging() {
        const intval = setInterval(() => {
            if (!serverConnecting || !server.state.get().connected)
                return;
            const pongTimeout = setTimeout(() => {
                if (serverConnecting) {
                    server.disconnect();
                    server.connect().catch(err => { });
                }
            }, PING_TIMEOUT);
            server.ping().then(() => clearTimeout(pongTimeout), err => console.log('Ping failed', err));
        }, PING_INTERVAL);
        return () => clearInterval(intval);
    }
    /**
     * Once there has been no activity for a certain amount of time, disconnect from the server. Reconnect as soon as
     * there is more activity again.
     */
    function hadActivity() {
        if (!shouldConnect) {
            return;
        }
        if (!serverConnecting) {
            console.log('Activity resumed, reconnecting');
            serverConnecting = true;
            server.connect().catch(err => { });
        }
        clearTimeout(activityTimeout);
        activityTimeout = setTimeout(() => {
            console.log('No activity for a while, disconnecting');
            serverConnecting = false;
            server.disconnect();
        }, INACTIVE_TIMEOUT);
    }
    function auth(idToken, delegate) {
        const args = [idToken];
        if (delegate)
            args.push(delegate);
        return server.auth(...args);
    }
    /**
     * Mark these docs as ones we are interested in syncing
     */
    async function sync(paths) {
        if (Array.isArray(paths))
            paths = new Set(paths);
        const subPaths = new Set(Array.from(paths).map(p => p.split('/').slice(0, 2).join('/')));
        Array.from(docSyncers.keys()).forEach(paths.delete, paths);
        Array.from(subbed).forEach(subPaths.delete, subPaths);
        if (!paths.size)
            return;
        paths.forEach(path => getDocSyncer(path));
        subPaths.forEach(subbed.add, subbed);
        if (authed.get()) {
            return startSyncing(paths, subPaths);
        }
    }
    /**
     * Begin syncing all the docs that we are interested in
     */
    async function startSyncing(paths, subPaths) {
        if (!authed.get())
            return;
        updateState({ syncing: 'initial' });
        // Delete pending deletes
        const deleted = await db.stores.deleted.getAll();
        await Promise.all(deleted.map(async ({ path }) => {
            await server.sendAfterAuthed('delete', path);
            const docSyncer = getDocSyncer(path);
            await docSyncer.docDeleted();
            removeDocSyncer(path);
        }));
        // Subscribe to the paths and sync the docs
        await Promise.all([
            subPaths.size && server.send('sub', Array.from(subPaths)),
            ...Array.from(paths).map(async (path) => {
                const docSyncer = getDocSyncer(path);
                await docSyncer.sync(); // Run initial sync
            }),
        ])
            .then(() => updateState({ syncing: null }))
            .catch(err => updateState({ syncing: err }));
    }
    /**
     * Unmark this doc as one we are interested in syncing
     */
    async function unsync(paths) {
        if (Array.isArray(paths))
            paths = new Set(paths);
        paths.forEach(path => !docSyncers.has(path) && paths.delete(path));
        if (!paths.size)
            return;
        await server.sendAfterAuthed('unsub', Array.from(paths));
        paths.forEach(path => removeDocSyncer(path));
    }
    async function changesCreated(path, changes) {
        if (!authed.get())
            return;
        const hasDocSyncer = docSyncers.has(path);
        if (!hasDocSyncer)
            await sync([path]);
        const docSyncer = getDocSyncer(path);
        updateState({ syncing: 'updates' });
        try {
            await docSyncer.send(changes);
            updateState({ syncing: null });
        }
        catch (err) {
            if (!(err instanceof Error))
                err = new Error(err + '');
            console.error(err);
            updateState({ syncing: err });
        }
    }
    async function docDeleted(path) {
        if (!authed.get())
            return;
        const docSyncer = getDocSyncer(path);
        updateState({ syncing: 'updates' });
        try {
            await server.sendAfterAuthed('delete', path);
            docSyncer.docDeleted();
            removeDocSyncer(path);
            updateState({ syncing: null });
        }
        catch (err) {
            if (!(err instanceof Error))
                err = new Error(err + '');
            console.error(err);
            updateState({ syncing: err });
        }
    }
    function onAuth(authed) {
        if (authed && !isSyncing) {
            isSyncing = true;
            startSyncing(new Set(docSyncers.keys()), subbed);
        }
        else if (!authed && isSyncing) {
            isSyncing = false;
        }
    }
    async function onMessage(data) {
        if (!data || typeof data.path !== 'string') {
            return;
        }
        let { path, changes, deleted } = data;
        const docSyncer = docSyncers.get(path); // Don't create a docSyncer if it isn't being used
        if (!docSyncer || !(deleted || Array.isArray(changes)))
            return;
        try {
            updateState({ syncing: 'updates' });
            if (Array.isArray(changes)) {
                await docSyncer.receive(changes);
            }
            else if (deleted === true) {
                unsync([path]);
                await docSyncer.docDeleted();
            }
            updateState({ syncing: 'updates' });
        }
        catch (err) {
            if (!(err instanceof Error))
                err = new Error(err + '');
            console.error(err);
            updateState({ syncing: err });
        }
    }
    function getDocSyncer(path) {
        let docSyncer = docSyncers.get(path);
        if (!docSyncer) {
            docSyncer = createDocSyncer(getDocConfig(config, path), tab, db, server, path);
            docSyncers.set(path, docSyncer);
        }
        return docSyncer;
    }
    function removeDocSyncer(path) {
        docSyncers.delete(path);
    }
}
